<main>
  <loading-overlay [isLoading]="isLoading() > 0" [fullWidth]="true"></loading-overlay>
  <p-toast position="bottom-right" key="br"></p-toast>
  <p-confirmDialog />

  <header>
    <div class="headline row-start-center gap-[1rem]">
      <h1>{{ headerTitle() }}</h1>
      @if (!isNew()) {
        <p-tag [severity]="statusSeverity()" [value]="statusLabelText()" />
      }
      @if (autoCreated()) {
        <p-tag severity="info" [value]="'ApAdvance_AutoCreated' | translateWF: 'Auto-created'" />
      }
      @if (requestId()) {
        <a class="text-primary cursor-pointer text-sm row-start-center gap-1" (click)="openRequest()">
          <i class="pi pi-arrow-up-right"></i> {{ 'ApAdvance_FromRequest' | translateWF: 'From request' }}
        </a>
      }
    </div>
    <div class="buttons">
      <!-- #5223 — a draft has posted nothing, so it can be removed outright. Hidden until the record is
           saved (there is nothing to delete before that) and again once it leaves Draft. The API applies
           the same rule; this only keeps the button off screen when it would be refused. -->
      @if (isDraft() && currentId()) {
        <p-button [label]="'Delete' | translateWF: 'Delete'" icon="pi pi-trash" severity="danger" variant="outlined" (click)="confirmDelete()" />
      }

      <span class="separator"></span>
      @if (isDraft() && showAdvanceForm()) {
        <p-button [label]="'SaveDraft' | translateWF: 'Save draft'" icon="pi pi-save" variant="outlined" (click)="saveDraft()" />
      }
      <!-- Post only after the draft is persisted (like AP documents): save first, then post from a bank account. -->
      @if (isDraft() && isPersisted() && showAdvanceForm()) {
        <p-button [label]="'Post' | translateWF: 'Post'" icon="pi pi-check" severity="success" (click)="post()" />
      }
      <p-button [label]="'Cancel' | translateWF: 'Cancel'" variant="outlined" icon="pi pi-arrow-left" (click)="back()" />
    </div>
  </header>

  <div class="content">
    @if (showRequestPicker()) {
      <!-- Advance Request path: choose an approved request; selecting it opens (or spawns) its draft advance. -->
      <p-panel [header]="'ApAdvance_Section_RequestPicker' | translateWF: 'Pay an approved advance request'">
        <p-card>
          <p class="text-sm text-surface-500 mb-3">
            {{ 'ApAdvance_RequestPicker_Hint' | translateWF: 'Choose an approved advance request to open its draft advance, then pay it from a bank account.' }}
          </p>
          <div class="form-box max-w-[40rem]">
            <label for="request">{{ 'ApAdvance_Request' | translateWF: 'Approved request' }}</label>
            <p-select
              id="request"
              [formControl]="selectedRequestControl"
              [options]="requestOptions()"
              optionLabel="label"
              optionValue="value"
              [filter]="true"
              [showClear]="true"
              appendTo="body"
              styleClass="w-full"
              [placeholder]="'ApAdvance_Request_Placeholder' | translateWF: 'Select an approved request'"
              (onChange)="onRequestSelected($event.value)"
            />
            @if (requestOptions().length === 0) {
              <small class="text-surface-500">{{ 'ApAdvance_NoApprovedRequests' | translateWF: 'No approved advance requests are awaiting payment.' }}</small>
            }
          </div>
        </p-card>
      </p-panel>
    } @else {
      <form [formGroup]="form" class="grid-12 gap-[1.5rem]">
        <!-- LEFT COLUMN -->
        <div class="span-12-12-8 col">
          <!-- Advance -->
          <p-panel [header]="'ApAdvance_Section_Advance' | translateWF: 'Advance'" [toggleable]="true">
            <p-card>
              <div class="grid-12">
                <div class="span-12">
                  <div class="form-box">
                    <label for="provider">{{ 'ApPayment_Provider' | translateWF: 'Provider' }} <span class="text-red-500">*</span></label>
                    <!-- `class` sizes the host, `styleClass` the inner input — the typeahead needs BOTH
                       to fill its column, matching Payment Request details (#5227/#5228). -->
                    <p-autoComplete
                      id="provider"
                      formControlName="provider"
                      #providerSearch="cslServerSearch"
                      [cslServerSearch]="fetchProviders"
                      [suggestions]="providerSearch.results()"
                      (onSelect)="onProviderSelected($event.value)"
                      (onClear)="onProviderCleared()"
                      optionLabel="name"
                      [forceSelection]="true"
                      [dropdown]="true"
                      dropdownIcon="pi pi-search"
                      [minLength]="1"
                      appendTo="body"
                      class="w-full"
                      styleClass="w-full"
                      [ngClass]="{ 'ng-invalid ng-dirty': form.controls.provider.invalid && form.controls.provider.touched }"
                    >
                      <ng-template let-business pTemplate="item">
                        <span
                          ><strong>{{ business.code }}</strong> — {{ business.name }}</span
                        >
                      </ng-template>
                    </p-autoComplete>
                    @if (fromRequest()) {
                      <small class="text-surface-500">
                        <i class="pi pi-lock text-xs"></i> {{ 'ApAdvance_Provider_FromRequest' | translateWF: 'Provider comes from the advance request and cannot be changed.' }}
                      </small>
                    } @else {
                      <small class="text-surface-500">{{ 'ApAdvance_Provider_Hint' | translateWF: 'Selecting a provider loads its purchase orders below.' }}</small>
                    }
                  </div>
                </div>

                <div class="span-12-6">
                  <div class="form-box">
                    <label for="currency">{{ 'ApPayment_Currency' | translateWF: 'Currency' }} <span class="text-red-500">*</span></label>
                    @if (canEditCurrency()) {
                      <p-select
                        id="currency"
                        formControlName="currency"
                        [options]="currencyOptions()"
                        optionLabel="label"
                        optionValue="value"
                        appendTo="body"
                        styleClass="w-full"
                      />
                      <small class="text-surface-500">{{ 'ApAdvance_Currency_Hint' | translateWF: 'The currency you are paying in — it filters the bank accounts.' }}</small>
                    } @else {
                      <input id="currency" type="text" pInputText class="w-full" [value]="currencyLabel(form.controls.currency.value)" [disabled]="true" />
                    }
                  </div>
                </div>
                <div class="span-12-6">
                  <div class="form-box">
                    <label for="paymentDate">{{ 'ApAdvance_Date' | translateWF: 'Advance date' }} <span class="text-red-500">*</span></label>
                    <p-datepicker
                      id="paymentDate"
                      formControlName="paymentDate"
                      [showIcon]="true"
                      appendTo="body"
                      styleClass="w-full"
                      [ngClass]="{ 'ng-invalid ng-dirty': form.controls.paymentDate.invalid && form.controls.paymentDate.touched }"
                    />
                  </div>
                </div>
              </div>
            </p-card>
          </p-panel>

          <!-- Bank, reference, charges & documents (shared component) -->
          <app-bank-charges-section
            [title]="'ApAdvance_Section_Bank' | translateWF: 'Account, reference, charges & documents'"
            [bankAccountControl]="form.controls.bankAccount"
            [referenceControl]="form.controls.bankTransactionReference"
            [bankingFeeControl]="form.controls.bankingFee"
            [securityFeeControl]="form.controls.securityFee"
            [bankAccountOptions]="bankAccountOptions()"
            [destinationControl]="form.controls.selectedProviderBankId"
            [destinationOptions]="providerBankOptions()"
            [destinationReadonly]="destinationLocked()"
            [destinationPlaceholder]="'ApAdvance_SelectDestination' | translateWF: 'Select a provider bank account'"
            [noDestinationHint]="
              'ApAdvance_NoDestination_Hint'
                | translateWF
                  : 'No account of this provider can receive this advance from the selected bank account — try another source account, or add one on the provider record.'
            "
            [currencyLabel]="currencyLabel(currency())"
            [attachments]="attachments()"
            [canAttach]="isPersisted()"
            [bankingFeeHint]="'ApAdvance_BankingFee_Hint' | translateWF: 'Wire / transfer charge — added to the total paid.'"
            [securityFeeHint]="'ApAdvance_SecurityFee_Hint' | translateWF: 'Insurance / handling — added to the total paid.'"
            (fileSelected)="onReceiptFile($event)"
            (attachmentOpened)="openAttachment($event)"
          >
            <div destinationFooter>
              <!-- Why this cannot go out, where that is so. The same rule the API applies at post, said on
                   the screen that shows the account rather than as a toast after the round-trip. -->
              @if (destinationConflict(); as conflict) {
                <p-message severity="error" styleClass="w-full mb-4">{{ conflict }}</p-message>
              }

              <!-- The one figure the officer states (#5373). Which leg it is follows from the currencies: the
                   obligation pins one end of the transfer and the other is the question. Owing Lempiras from a
                   dollar account, the provider gets every Lempira owed and what LEFT is asked; owing dollars
                   into a provider banking in Lempiras, USD 500 leaves and what ARRIVED is asked. -->
              @if (observedLeg() !== 'none') {
                <div class="observed-leg mb-4">
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                    <div class="form-box">
                      <label>{{ 'ApAdvance_AmountRequested' | translateWF: 'Amount requested' }}</label>
                      <input type="text" pInputText class="w-full" [value]="(obligationTotal() | number: '1.2-2') + ' ' + currencyLabel(obligationCurrency())" [disabled]="true" />
                      <small class="text-surface-500 block">
                        {{ 'ApAdvance_AmountRequested_Hint' | translateWF: 'What the approved purchase orders owe. Not editable.' }}
                      </small>
                    </div>
                    <div class="form-box">
                      <label>
                        @if (observedLeg() === 'departure') {
                          {{ 'ApAdvance_AmountSent' | translateWF: 'Amount sent from bank' }}
                        } @else {
                          {{ 'ApAdvance_AmountReceived' | translateWF: 'Amount received by the provider' }}
                        }
                      </label>
                      <p-inputNumber
                        formControlName="observedAmount"
                        mode="decimal"
                        [minFractionDigits]="2"
                        [maxFractionDigits]="2"
                        [min]="0"
                        [suffix]="' ' + currencyLabel(observedCurrency())"
                        styleClass="w-full"
                        [ngClass]="{ 'ng-invalid ng-dirty': !form.controls.observedAmount.value }"
                      />
                      <small class="text-surface-500 block">
                        @if (observedLeg() === 'departure') {
                          {{
                            'ApAdvance_AmountSent_Hint'
                              | translateWF: 'The exact amount that left the account, from the bank confirmation. The exchange rate is calculated from it.'
                          }}
                        } @else {
                          {{
                            'ApAdvance_AmountReceived_Hint'
                              | translateWF: 'The exact amount that reached the provider, from the bank confirmation. The exchange rate is calculated from it.'
                          }}
                        }
                      </small>
                    </div>
                  </div>

                  @if (derivedRate() !== null) {
                    <!-- Red because this IS the rate the books will use, not because anything is wrong: nothing
                         else supplies it, so a mistyped amount is a silently wrong rate on the Anticipo. -->
                    <div class="mt-3 p-3 border-round border-1 border-red-200 bg-red-50">
                      <div class="text-red-700 font-medium">{{ 'ApAdvance_RateUsed' | translateWF: 'This is the exchange rate being used' }}: {{ derivedRateLabel() }}</div>
                      <div class="text-sm text-red-600">
                        {{ form.controls.observedAmount.value | number: '1.2-2' }} {{ currencyLabel(observedCurrency()) }} &divide; {{ obligationTotal() | number: '1.2-2' }}
                        {{ currencyLabel(obligationCurrency()) }}
                        @if (dayRate(); as day) {
                          &middot; {{ 'ApAdvance_RateOfDay' | translateWF: 'Rate of the day' }}: {{ day | number: '1.2-4' }}
                          @if (rateDeviation(); as deviation) {
                            ({{ deviation > 0 ? '+' : '' }}{{ deviation * 100 | number: '1.2-2' }}%)
                          }
                        }
                      </div>
                    </div>
                  }

                  <!-- A separate, louder warning: a slipped decimal balances perfectly and is invisible
                       afterwards, so it cannot share the informational styling above.
                       The fallback is double-quoted because it contains an apostrophe: written as `&apos;`
                       the tokenizer decodes the entity before parsing the expression, the string literal
                       ends mid-sentence, and Angular silently renders the whole `{{ }}` as text. -->
                  @if (rateLooksWrong()) {
                    <p-message severity="warn" styleClass="w-full mt-2">
                      {{ 'ApAdvance_RateFarFromDay' | translateWF: "That rate is a long way from the day's. Check the amount against the bank confirmation before posting." }}
                    </p-message>
                  }
                </div>
              }
            </div>
          </app-bank-charges-section>

          <!-- Allocate to purchase orders -->
          <p-panel [toggleable]="true">
            <ng-template pTemplate="header">
              <div class="row-between-center w-full">
                <span class="font-semibold">{{ 'ApAdvance_Section_Allocate' | translateWF: 'Allocate to purchase orders' }} <span class="text-red-500">*</span></span>
                <span class="text-sm text-surface-500">
                  {{ selectedCount() }} {{ 'ApPayment_Of' | translateWF: 'of' }} {{ totalPos() }} {{ 'ApAdvance_PosSelected' | translateWF: 'purchase orders selected' }}
                </span>
              </div>
            </ng-template>

            <p-card data-variant="compact">
              @if (fromRequest()) {
                <div class="row-sm-start-center mb-3 p-3 rounded bg-surface-100 text-surface-600 text-sm">
                  <i class="pi pi-info-circle"></i>
                  <span>{{
                    'ApAdvance_Allocate_FromRequest'
                      | translateWF
                        : 'The provider and purchase orders come from the advance request. You can still adjust the amount advanced per PO (e.g. to advance less than requested).'
                  }}</span>
                </div>
              }

              <p-table [value]="allocations.controls">
                <ng-template pTemplate="header">
                  <tr>
                    <th style="width: 3rem"></th>
                    <th>{{ 'ApAdvance_PurchaseOrder' | translateWF: 'Purchase order' }}</th>
                    <th class="text-right">{{ 'ApAdvance_Ordered' | translateWF: 'Ordered' }}</th>
                    <th class="text-right" style="width: 16rem">{{ 'ApAdvance_Amount' | translateWF: 'Advance amount' }}</th>
                  </tr>
                </ng-template>

                <ng-template pTemplate="body" let-row>
                  @let alloc = getRowGroup(row);
                  <tr [formGroup]="alloc">
                    <td><p-checkbox formControlName="selected" [binary]="true" /></td>
                    <td>
                      <a class="text-primary cursor-pointer" (click)="openPurchaseOrder(alloc.controls.purchaseOrderReference.value)">
                        {{ alloc.controls.purchaseOrderNumber.value }} <i class="pi pi-external-link text-xs"></i>
                      </a>
                    </td>
                    <!-- The ORDER's currency, not the advance's: what we owe is denominated in the purchase
                         order and does not change because the money is leaving a Lempira account (#5373). -->
                    <td class="text-right text-surface-500">
                      {{ alloc.controls.ordered.value | number: '1.2-2' }} {{ currencyLabel(alloc.controls.currency.value ?? obligationCurrency()) }}
                    </td>
                    <td class="text-right">
                      <p-inputNumber
                        formControlName="allocatedAmount"
                        mode="decimal"
                        [minFractionDigits]="2"
                        [maxFractionDigits]="2"
                        [min]="0"
                        [suffix]="' ' + currencyLabel(alloc.controls.currency.value ?? obligationCurrency())"
                        styleClass="w-full"
                      />
                    </td>
                  </tr>
                </ng-template>

                <ng-template pTemplate="emptymessage">
                  <tr>
                    <td colspan="4">{{ 'ApAdvance_NoPos' | translateWF: 'Select a provider to load its purchase orders.' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </p-card>
          </p-panel>

          <!-- Notes -->
          <p-panel [header]="'ApPayment_Section_Notes' | translateWF: 'Notes'" [toggleable]="true" [collapsed]="true">
            <p-card>
              <div class="form-box">
                <textarea pTextarea formControlName="notes" rows="3" class="w-full" placeholder="Add a note…"></textarea>
              </div>
            </p-card>
          </p-panel>
        </div>

        <!-- RIGHT COLUMN -->
        <div class="span-12-12-4 col sticky-summary">
          <app-settlement-summary
            [title]="'ApAdvance_Section_Summary' | translateWF: 'Advance summary'"
            [hint]="summaryHint()"
            [baseLabel]="'ApAdvance_Summary_AmountSent' | translateWF: 'Amount sent'"
            [baseAmount]="amount()"
            [bankingFeeLabel]="'ApPayment_BankingFee' | translateWF: 'Banking fee'"
            [securityFeeLabel]="'ApPayment_SecurityFee' | translateWF: 'Security fee'"
            [bankingFee]="bankingFeeValue()"
            [securityFee]="securityFeeValue()"
            [feesReduceNet]="false"
            [netLabel]="'ApAdvance_TotalCashOut' | translateWF: 'Total cash out'"
            [net]="totalCashOut()"
            [currencyLabel]="currencyLabel(currency())"
          />

          <!-- What we owe, order by order, and the rate each was struck at (#5373).
               When nothing converts these rates ARE the ones used — several orders at different rates each
               keep their own, and the blended figure below is an output. When the bank converted there is
               one rate for the whole transfer, and these show where the approved value came from, so the
               gap to what it actually cost is visible. -->
          @if (obligationBreakdown().length > 0) {
            <p-panel [header]="'ApAdvance_Section_Obligation' | translateWF: 'What we owe'">
              <div class="summary-rows">
                @for (row of obligationBreakdown(); track row.purchaseOrderNumber) {
                  <div class="summary-rows__row flex items-start">
                    <span>
                      {{ row.amount | number: '1.2-2' }} {{ currencyLabel(obligationCurrency()) }}
                      <span class="block text-[0.7rem] text-surface-500">{{ row.purchaseOrderNumber }}</span>
                    </span>
                    <span class="ml-auto text-right">
                      {{ 'ApAdvance_StruckAt' | translateWF: 'at' }} {{ row.rate | number: '1.2-4' }}
                      <span class="block text-[0.7rem] text-surface-500"> {{ row.functionalAmount | number: '1.2-2' }} {{ currencyLabel(functionalCurrency()) }} </span>
                    </span>
                  </div>
                }
                <div class="summary-rows__row flex items-center font-medium">
                  <span>{{ 'ApAdvance_ApprovedTotal' | translateWF: 'Approved total' }}</span>
                  <span class="ml-auto"> {{ approvedFunctionalTotal() | number: '1.2-2' }} {{ currencyLabel(functionalCurrency()) }} </span>
                </div>
              </div>
            </p-panel>
          }

          <!-- Accounting (#5024 #5031) -->
          @if (showAccounting()) {
            <p-panel [header]="'ApAdvance_Section_Accounting' | translateWF: 'Accounting'">
              <p-card>
                <div class="summary-rows">
                  <div class="summary-rows__row row-start-center">
                    <span>{{ 'ApDocument_JournalEntry' | translateWF: 'Journal entry' }}</span>
                    <span class="ml-auto font-mono">{{ journalEntryDocumentNumber() }}</span>
                  </div>
                </div>

                <span class="block mt-3 mb-2 text-[0.7rem] font-semibold uppercase tracking-wide text-surface-500">
                  {{ 'ApDocument_GlImpact' | translateWF: 'GL impact' }}
                </span>
                <app-journal-entry-summary [references]="journalEntryReferences()" [currencyLabel]="currencyLabel(currency())" />
              </p-card>
            </p-panel>
          }
        </div>
      </form>
    }
  </div>
</main>
